import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ExternalLink, RefreshCw, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Callout,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Section,
  Skeleton,
  StatusDot,
  Switch,
  Table,
  TableContainer,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  useToast,
} from '@zentera/ui';
import { api, ApiError, fmt } from '@zentera/web-core';
import { usePageTitle } from '../components/shell';
import { useCan } from '../lib/auth';

interface WebsiteState {
  source: {
    url: string;
    autoRefresh: boolean;
    status: 'idle' | 'running' | 'done' | 'error';
    lastCrawlAt: string | null;
    lastError: string | null;
    lastStats: { pages: number; created: number; changed: number; removed: number; skipped: number; robotsBlocked: number; usedSitemap: boolean } | null;
  } | null;
  pages: { id: string; url: string; title: string; include: boolean; change: 'new' | 'changed' | 'same'; chars: number; preview: string; fetchedAt: string }[];
}

const shortUrl = (u: string) => u.replace(/^https?:\/\//, '');

export function WebsitePage() {
  usePageTitle('Site do provedor');
  const can = useCan();
  const editable = can('knowledge.write');
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['website'],
    queryFn: () => api.get<WebsiteState>('/api/knowledge/website'),
    // Durante a leitura, acompanha o andamento.
    refetchInterval: (query) => (query.state.data?.source?.status === 'running' ? 2000 : false),
  });
  const src = q.data?.source ?? null;
  const [url, setUrl] = useState('');
  const [auto, setAuto] = useState(true);
  useEffect(() => {
    if (src) {
      setUrl(src.url);
      setAuto(src.autoRefresh);
    }
  }, [src?.url, src?.autoRefresh]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => void qc.invalidateQueries({ queryKey: ['website'] });
  const onError = (title: string) => (e: unknown) => toast({ tone: 'danger', title, description: e instanceof ApiError ? e.message : undefined });

  const save = useMutation({
    mutationFn: () => api.put('/api/knowledge/website', { url, autoRefresh: auto }),
    onSuccess: () => {
      refresh();
      toast({ tone: 'success', title: 'Site salvo' });
    },
    onError: onError('Não foi possível salvar'),
  });
  const crawl = useMutation({
    mutationFn: async () => {
      if (!src || src.url !== url || src.autoRefresh !== auto) await api.put('/api/knowledge/website', { url, autoRefresh: auto });
      return api.post('/api/knowledge/website/crawl');
    },
    onSuccess: refresh,
    onError: onError('Não foi possível ler o site'),
  });
  const toggle = useMutation({
    mutationFn: (p: { id: string; include: boolean }) => api.patch(`/api/knowledge/website/pages/${p.id}`, { include: p.include }),
    onSuccess: refresh,
    onError: onError('Não foi possível alterar a página'),
  });
  const remove = useMutation({
    mutationFn: () => api.del('/api/knowledge/website'),
    onSuccess: () => {
      setUrl('');
      refresh();
      toast({ tone: 'success', title: 'Site removido do conhecimento', description: 'Publique o conhecimento para o assistente deixar de usar as páginas.' });
    },
    onError: onError('Não foi possível remover'),
  });

  if (q.isError) return <Callout tone="danger" title="Não foi possível carregar">Atualize a página.</Callout>;
  if (!q.data) return <Skeleton className="h-96 rounded-lg" />;
  const running = src?.status === 'running' || crawl.isPending;
  const pages = q.data.pages;
  const used = pages.filter((p) => p.include).length;
  const s = src?.lastStats;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Site do provedor"
        description="O assistente pode usar o conteúdo do seu site (planos, cobertura, perguntas frequentes, políticas). O que você cadastra nas outras telas vale mais que o site, que pode estar desatualizado."
      />

      <Section title="Endereço do site" description="Lemos apenas páginas deste domínio, respeitando o robots.txt, até 50 páginas. Só o texto é guardado.">
        <div className="max-w-2xl space-y-4">
          <Field label="Endereço">
            <Input value={url} disabled={!editable || running} placeholder="https://www.seuprovedor.com.br" onChange={(e) => setUrl(e.target.value)} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-fg-2">
            <Switch label="Reler o site toda semana" checked={auto} disabled={!editable || running} onCheckedChange={setAuto} />
            Reler o site automaticamente toda semana (as mudanças ficam pendentes até a próxima publicação)
          </label>
          {editable && (
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" icon={<RefreshCw />} loading={running} disabled={!url.trim()} onClick={() => crawl.mutate()}>
                {src?.lastCrawlAt ? 'Ler de novo' : 'Ler site'}
              </Button>
              {src && (src.url !== url || src.autoRefresh !== auto) && (
                <Button variant="secondary" loading={save.isPending} disabled={running} onClick={() => save.mutate()}>
                  Salvar sem ler
                </Button>
              )}
              {src && (
                <Button
                  variant="ghost"
                  icon={<Trash2 />}
                  disabled={running}
                  onClick={() => {
                    if (window.confirm('Remover o site e as páginas lidas? O assistente deixa de usá-las na próxima publicação.')) remove.mutate();
                  }}
                >
                  Remover site
                </Button>
              )}
            </div>
          )}
        </div>
      </Section>

      {src && (
        <Section title="Última leitura">
          {running ? (
            <p className="flex items-center gap-2 text-sm text-fg-2">
              <StatusDot tone="info">Lendo o site…</StatusDot> pode levar até 2 minutos. Você pode sair desta tela.
            </p>
          ) : src.status === 'error' ? (
            <Callout tone="danger" title="A leitura não foi concluída">
              {src.lastError} Confira se o endereço abre no navegador e tente de novo.
            </Callout>
          ) : s ? (
            <div className="space-y-3">
              <p className="text-sm text-fg-2">
                {src.lastCrawlAt && fmt.dateTime(src.lastCrawlAt)} · {s.pages} {s.pages === 1 ? 'página lida' : 'páginas lidas'}
                {s.created ? ` · ${s.created} nova(s)` : ''}
                {s.changed ? ` · ${s.changed} alterada(s)` : ''}
                {s.removed ? ` · ${s.removed} removida(s)` : ''}
                {s.robotsBlocked ? ` · ${s.robotsBlocked} bloqueada(s) pelo robots.txt` : ''}
                {s.skipped ? ` · ${s.skipped} ignorada(s)` : ''}
              </p>
              {(s.created > 0 || s.changed > 0 || s.removed > 0) && (
                <Callout tone="info" title="Há conteúdo novo do site">
                  O assistente passa a usar estas páginas na próxima publicação. <Link className="underline underline-offset-2" to="/assistant">Publicar conhecimento</Link>
                </Callout>
              )}
            </div>
          ) : (
            <p className="text-sm text-fg-3">O site ainda não foi lido.</p>
          )}
        </Section>
      )}

      {pages.length > 0 ? (
        <Section title="Páginas encontradas" description={`${used} de ${pages.length} em uso pelo assistente. Desmarque páginas que não ajudam no atendimento (blog, vagas, notícias).`}>
          <TableContainer>
            <Table>
              <THead>
                <tr>
                  <Th>Usar</Th>
                  <Th>Página</Th>
                  <Th align="right">Texto</Th>
                  <Th>Situação</Th>
                </tr>
              </THead>
              <TBody>
                {pages.map((p) => (
                  <Tr key={p.id}>
                    <Td>
                      <Switch label={`Usar ${p.title}`} checked={p.include} disabled={!editable || toggle.isPending} onCheckedChange={(v) => toggle.mutate({ id: p.id, include: v })} />
                    </Td>
                    <Td className="max-w-md">
                      <p className="truncate text-sm text-fg" title={p.preview}>{p.title}</p>
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1 truncate text-xs text-fg-3 hover:underline">
                        {shortUrl(p.url)} <ExternalLink className="size-3 shrink-0" />
                      </a>
                    </Td>
                    <Td align="right" className="tabular text-xs text-fg-3">{(p.chars / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil caracteres</Td>
                    <Td>{p.change === 'new' ? <Badge tone="info">Nova</Badge> : p.change === 'changed' ? <Badge tone="warning">Alterada</Badge> : <span className="text-xs text-fg-4">Sem mudança</span>}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        </Section>
      ) : (
        !running && src?.status !== 'error' && <EmptyState compact title="Nenhuma página lida ainda" description="Informe o endereço e clique em “Ler site”." />
      )}
    </div>
  );
}
